// Metals Dynamic Pricing — front-end display + cart wiring
// - Reads metafields from data-* attributes on the product DOM
// - CC price = melt + premium
// - Wire price = CC / 1.03  (≈ 3% lower)
// - Keeps your spot-price polling + UI updates

class MetalsPricing {
  constructor() {
    // If you expose your metal-prices worker over workers.dev, put that URL here.
    // (The service binding is server-to-server; the browser still calls a public URL.)
    this.apiUrl = 'https://metal-prices.delicate-frog-515b.workers.dev/';

    // Fallbacks (overwritten by API)
    this.spotPrices = { gold: 2750, silver: 30.5, platinum: 945, palladium: 965 };

    this.lastUpdated = null;
    this.cacheTimeout = 300000; // 5 minutes
    this.isLoading = false;

    // For cart integration (kept from your original)
    this.productPrices = {};

    console.log('MetalsPricing initialized');
    this.init();
  }

  // ---------- utils ----------
  toNumber(v) {
    if (v == null) return 0;
    const cleaned = String(v).trim().replace(/[^0-9.\-]/g, '');
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  }
  toLowerStr(v, fallback = '') {
    return (v == null ? fallback : String(v)).trim().toLowerCase();
  }
  formatPrice(price, decimals = 2) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(Math.max(0, Number(price) || 0));
  }

  // ---------- lifecycle ----------
  async init() {
  console.log('Initializing metals pricing...');

  // Render immediately with fallbacks, hook forms, then fetch real prices
  this.updateProductPrices();
  this.setupCartIntegration();
  await this.fetchSpotPrices();

  // Polling control
  const startPolling = () => {
    if (this._poll) return;
    this._poll = setInterval(() => this.fetchSpotPrices(), this.cacheTimeout);
  };
  const stopPolling = () => {
    if (this._poll) { clearInterval(this._poll); this._poll = null; }
  };

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      stopPolling();
    } else {
      this.fetchSpotPrices(); // refresh immediately on return
      startPolling();
    }
  });

  if (!document.hidden) startPolling();
}


  // ---------- cart wiring (unchanged behavior) ----------
  setupCartIntegration() {
    console.log('Setting up cart integration...');
    const cartForms = document.querySelectorAll('form[action*="/cart/add"]');
    console.log('Found cart forms:', cartForms.length);

    cartForms.forEach((form) => {
      form.addEventListener('submit', (event) => this.handleCartSubmission(event, form));
    });
    document.addEventListener('submit', (event) => {
      if (event.target.matches('form[action*="/cart/add"]')) {
        this.handleCartSubmission(event, event.target);
      }
    });
  }

  handleCartSubmission(event, form) {
    console.log('Cart form submission intercepted');
    const dynamicContainer =
      form.closest('[data-dynamic-pricing="true"]') ||
      form.querySelector('[data-dynamic-pricing="true"]') ||
      document.querySelector('[data-dynamic-pricing="true"]');

    if (!dynamicContainer) {
      console.log('No dynamic pricing container found, allowing normal submission');
      return;
    }
    const variantId = form.querySelector('input[name="id"]')?.value;
    if (!variantId) {
      console.log('No variant ID found, allowing normal submission');
      return;
    }
    const calculatedPrice = this.productPrices[variantId] || this.productPrices.creditCard;
    if (!calculatedPrice) {
      console.log('No calculated price found, allowing normal submission');
      return;
    }
    console.log('Adding dynamic price to cart submission:', calculatedPrice);
    this.lastCalculatedPrice = calculatedPrice;
  }

  // ---------- data fetch ----------
  async fetchSpotPrices() {
    if (this.isLoading) {
      console.log('Already fetching prices, skipping...');
      return;
    }
    this.isLoading = true;
    console.log('Fetching spot prices from metal-prices worker...');

    try {
      const res = await fetch(this.apiUrl, { headers: { 'Accept': 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      console.log('API response:', data);

      if (data?.metals) {
        for (const metal of Object.keys(this.spotPrices)) {
          const mid = data.metals[metal]?.mid;
          if (mid) this.spotPrices[metal] = Number(mid);
        }
        this.metalsData = data.metals;
        this.dataSource = data.source || 'metalpriceapi';
      } else {
        console.warn('No metals in response; keeping fallbacks');
        this.dataSource = 'fallback';
      }
      this.lastUpdated = new Date();

      document.dispatchEvent(new CustomEvent('metalsPricesUpdated', {
        detail: {
          prices: this.spotPrices,
          metalsData: this.metalsData,
          source: this.dataSource,
          lastUpdated: this.lastUpdated
        }
      }));

      this.updateProductPrices();
    } catch (err) {
      console.error('Failed to fetch spot prices:', err);
      this.dataSource = 'fallback';
      this.lastUpdated = new Date();
      this.updateProductPrices();
      this.handleFetchError(err);
    } finally {
      this.isLoading = false;
    }
  }

  // ---------- pricing core ----------
// Returns { cc, wire, meltValue }
computePrices({ metalType, meltWeight, premiumType, premiumPercent, premiumAmount }) {
  const metalKey = this.toLowerStr(metalType);
  const spot = this.spotPrices[metalKey];

  if (!spot) {
    console.warn(`Spot price not available for ${metalType}`);
    return { cc: 0, wire: 0, meltValue: 0 };
  }

  // 1) Melt
  const weight = this.toNumber(meltWeight) || 1.0; // assume toz
  const meltValue = spot * weight;

  // 2) Product premium (applies to WIRE base)
  const pType = this.toLowerStr(premiumType, 'percent');
  const pPct  = this.toNumber(premiumPercent);
  const pAmt  = this.toNumber(premiumAmount);

  let wire; // <-- WIRE is the base
  if (pType === 'percent') {
    wire = meltValue * (1 + (pPct / 100));
    console.log(`Using percentage premium for WIRE: ${pPct}%`);
  } else {
    wire = meltValue + pAmt;
    console.log(`Using fixed premium for WIRE: ${pAmt}`);
  }

  // 3) CC surcharge over WIRE (set your site-wide surcharge here)
  const ccSurchargePct = 3.5;                 // <<< CC is 3.5% above wire
  const cc = wire * (1 + ccSurchargePct / 100);

  // 4) Round for display
  const ccRounded   = Math.round(cc * 100) / 100;
  const wireRounded = Math.round(wire * 100) / 100;
  const meltRounded = Math.round(meltValue * 100) / 100;

  return { cc: ccRounded, wire: wireRounded, meltValue: meltRounded };
}


// ---------- DOM update (update ALL wrappers, not just one) ----------
updateProductPrices() {
  console.log('Updating product prices...');

  const containers = document.querySelectorAll('[data-dynamic-pricing="true"]');
  console.log('Found dynamic pricing elements:', containers.length);

  let lastCC = 0, lastWire = 0;

  containers.forEach((el, idx) => {
    // Read metafields from data-* attributes
    const metalType      = el.dataset.metalType;                   // e.g., "gold"
    const meltWeight     = el.dataset.meltWeight;                  // e.g., "1.0"
    const premiumType    = el.dataset.premiumType || 'Percent';    // "Percent" | "Dollar"/"Amount"
    const premiumPercent = el.dataset.premiumPercent;              // e.g., "1.5"
    const premiumAmount  = this.toNumber(el.dataset.premiumAmount);// e.g., "$25.00" -> 25

    // Skip if this block isn't configured
    if (!metalType) {
      console.warn('No metal type on element:', el);
      return;
    }

    // Compute prices (WIRE is base in your current logic; CC adds surcharge)
    const { cc, wire, meltValue } = this.computePrices({
      metalType, meltWeight, premiumType, premiumPercent, premiumAmount
    });

    console.log(`Element ${idx} computed — Melt: ${meltValue}, CC: ${cc}, Wire: ${wire}`);

    // Grab the current variant id from the add-to-cart form (if present)
    const variantId = document.querySelector('form[action*="/cart/add"] input[name="id"]')?.value;

    // ---- 1) Initial render using computed values ----
    el.querySelectorAll('.dynamic-price, [data-role="cc-price"]').forEach(node => {
      node.textContent = this.formatPrice(cc);
    });
    el.querySelectorAll('.wire-price, [data-role="wire-price"]').forEach(node => {
      node.textContent = this.formatPrice(wire);
    });
    // Optional: melt display
    el.querySelectorAll('.melt-value').forEach(node => {
      node.textContent = this.formatPrice(meltValue);
    });

    // Remember the latest (they should be identical across blocks)
    lastCC = cc;
    lastWire = wire;

    // ---- 2) Snap PDP to the variant price for exact PDP=Cart match (async) ----
    if (variantId) {
      this.fetchVariantPriceCents(variantId)
        .then(cents => {
          if (!Number.isFinite(cents)) return;

          const ccFromVariant = Math.round(cents) / 100;
          // If our computed CC differs from Shopify's variant by > 2¢, trust the variant
          if (Math.abs(ccFromVariant - cc) > 0.02) {
            const ccSurchargePct = 3.5; // keep in sync with your surcharge policy
            const wireFromVariant = Math.round(
              (ccFromVariant / (1 + ccSurchargePct / 100)) * 100
            ) / 100;

            // Re-render with the variant-derived numbers
            el.querySelectorAll('.dynamic-price, [data-role="cc-price"]').forEach(node => {
              node.textContent = this.formatPrice(ccFromVariant);
            });
            el.querySelectorAll('.wire-price, [data-role="wire-price"]').forEach(node => {
              node.textContent = this.formatPrice(wireFromVariant);
            });

            // Update the "latest" cache so cart handlers show the same
            lastCC = ccFromVariant;
            lastWire = wireFromVariant;

            console.log('[MetalsPricing] Snapped PDP to variant price for exact PDP=Cart match:', ccFromVariant);
          }
        })
        .catch(() => {
          // Ignore network hiccups
        });
    }
  }); // <-- this closes containers.forEach((el, idx) => { ... })

  // Store for cart / other scripts
  if (lastCC || lastWire) {
    this.productPrices.creditCard  = lastCC;
    this.productPrices.wireTransfer = lastWire;
  }

  // Update any global spot price badges/bars
  this.updateSpotPriceDisplays();
} // <-- closes updateProductPrices()



  // ---------- (placeholder) Shopify update hook ----------
  updateProductPricesInShopify(creditCardPrice, wireTransferPrice) {
    // Intentionally no-op on the front-end; your Cloudflare Worker updates Shopify variants.
    console.log('Would update Shopify products with:', { creditCardPrice, wireTransferPrice });
  }

  // ---------- global spot UI ----------
  updateSpotPriceDisplays() {
    Object.keys(this.spotPrices).forEach((metal) => {
      document.querySelectorAll(`[data-spot-price="${metal}"]`).forEach((el) => {
        el.textContent = this.formatPrice(this.spotPrices[metal]);
        if (this.metalsData?.[metal]) {
          const m = this.metalsData[metal];
          el.setAttribute('data-bid', m.bid ?? '');
          el.setAttribute('data-ask', m.ask ?? '');
          el.setAttribute('data-change', m.change ?? '');
          el.setAttribute('data-change-pct', m.changePct ?? '');
        }
      });
    });

    document.querySelectorAll('.spot-prices-updated').forEach((el) => {
      if (this.lastUpdated) {
        const t = this.lastUpdated.toLocaleTimeString();
        const src = this.dataSource === 'fallback' ? ' (cached)' : ' (live)';
        el.textContent = `Updated: ${t}${src}`;
      } else {
        el.textContent = 'Loading...';
      }
    });
  }

  handleFetchError(error) {
    document.querySelectorAll('.spot-price-error').forEach((el) => {
      el.style.display = 'block';
      el.textContent = `Using cached pricing (${this.lastUpdated ? this.lastUpdated.toLocaleString() : 'system startup'})`;
    });
  }

  // ---------- public helpers ----------
  getSpotPrice(metal) {
    return this.spotPrices[this.toLowerStr(metal)] ?? null;
  }
  getMetalData(metal) {
    return this.metalsData?.[this.toLowerStr(metal)] ?? null;
  }
  getCurrentPrices() {
    return { creditCard: this.productPrices.creditCard, wireTransfer: this.productPrices.wireTransfer };
  }
  async refreshPrices() {
    console.log('Manual price refresh requested');
    await this.fetchSpotPrices();
  }
  updateTestPrice(metal, price) {
    this.spotPrices[this.toLowerStr(metal)] = Number(price) || 0;
    this.updateProductPrices();
    console.log(`Manually updated ${metal} to ${this.formatPrice(price)}`);
  }

async fetchVariantPriceCents(variantId) {
    try {
      const r = await fetch(`/variants/${variantId}.json`, { headers: { Accept: 'application/json' } });
      if (!r.ok) return null;
      const j = await r.json();
      const price = Number(j?.variant?.price);
      return Number.isFinite(price) ? Math.round(price * 100) : null;
    } catch { return null; }
  }
}

// Boot
document.addEventListener('DOMContentLoaded', () => {
  console.log('DOM loaded, initializing MetalsPricing…');
  window.metalsPricing = new MetalsPricing();
});

// Node/CommonJS export (optional)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = MetalsPricing;
}
